import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useColorMode,
  useBreakpointValue,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react"
import {
  FiMenu,
  FiX,
  FiChevronRight,
} from 'react-icons/fi'
import { FaSun, FaMoon, FaFacebookF } from 'react-icons/fa'
import { NavLink, useLocation } from 'react-router-dom'
import { prefetchRoute } from '../../utils/routePrefetch'

export default function Header() {
  const { isOpen, onToggle } = useDisclosure()
  const { colorMode, toggleColorMode } = useColorMode()
  const [isScrolled, setIsScrolled] = useState(false)
  
  // Track scroll position for sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      transition="all 0.3s ease"
    >
      <Flex
        bg={useColorModeValue("red.600", "gray.900")}
        color={useColorModeValue("gray.600", "white")}
        minH={isScrolled ? "50px" : "60px"}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={"solid"}
        borderColor={useColorModeValue("red.700", "gray.900")}
        align={"center"}
        boxShadow={isScrolled ? "lg" : "none"}
        transition="all 0.3s ease"
      >
        <Flex
          flex={{ base: 1, md: "auto" }}
          ml={{ base: -2 }}
          display={{ base: "flex", md: "none" }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <FiX w={3} h={3} /> : <FiMenu w={5} h={5} />
            }
            variant={"ghost"}
            aria-label={"Toggle Navigation"}
          />
        </Flex>
        <Flex flex={{ base: 1 }} justify={{ base: "center", md: "start" }}>
          <Text
            textAlign={useBreakpointValue({ base: "center", md: "left" })}
            fontFamily={"heading"}
            color={useColorModeValue("white", "white")}
          >
            Hidatsa Áabaci
          </Text>

          <Flex display={{ base: "none", md: "flex" }} ml={10}>
            <DesktopNav />
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={"flex-end"}
          direction={"row"}
          spacing={6}
          pr={4}
        >
          <Link
            href="https://facebook.com/miaagarishda.hidatsa.3?refid=13&__tn__=%2Cg" isExternal>
            <Tooltip label="Facebook" aria-label="Facebook">
              <IconButton 
                icon={<FaFacebookF />}
                isRound='true' 
                colorScheme='facebook'
                />
            </Tooltip>
          </Link>
          <Tooltip label="Toggle Color Mode" aria-label="Toggle Color Mode">
            <IconButton icon={colorMode === "light" ? <FaMoon /> : <FaSun />}
              isRound='true'
              onClick={toggleColorMode}
              colorScheme={colorMode === "light" ? 'purple' : 'yellow' }
              >
            </IconButton>
          </Tooltip>
        </Stack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav onClose={onToggle} />
      </Collapse>
    </Box>
  )
}

const DesktopNav = () => {
  const linkColor = useColorModeValue("gray.100", "gray.200")
  const linkHoverColor = useColorModeValue("red.400", "gray.700")
  const popoverContentBgColor = useColorModeValue("white", "gray.800")
  const location = useLocation()

  // Prefetch route on hover
  const handleMouseEnter = useCallback((href) => {
    if (href) {
      prefetchRoute(href);
    }
  }, []);

  return (
    <Stack direction={"row"} spacing={4} >
      {NAV_ITEMS.map(navItem => {
        const isActive = location.pathname === navItem.href || 
          location.pathname.startsWith(navItem.href + '/')
        
        return (
          <Box key={navItem.label} onMouseEnter={() => handleMouseEnter(navItem.href)}>
            <Popover trigger={"hover"} placement={"bottom-start"}>
              <PopoverTrigger>
                <Link
                  as={NavLink}
                  p={2}
                  to={navItem.href ?? "#"}
                  fontSize={"sm"}
                  fontWeight={isActive ? 700 : 500}
                  color={linkColor}
                  rounded={"md"}
                  position="relative"
                  _hover={{
                    textDecoration: "none",
                    bg: linkHoverColor
                  }}
                  _activeLink={{
                    bg: linkHoverColor
                  }}
                  _after={isActive ? {
                    content: '""',
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80%',
                    height: '3px',
                    bg: 'white',
                    borderRadius: 'full'
                  } : {}}
                >
                  {navItem.label}
                </Link>
              </PopoverTrigger>

            {navItem.children && (
              <PopoverContent
                border={0}
                boxShadow={"xl"}
                bg={popoverContentBgColor}
                p={4}
                rounded={"xl"}
                minW={"sm"}
              >
                <Stack>
                  {navItem.children.map(child => (
                    <DesktopSubNav key={child.label} parentHref={navItem.href} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            )}
          </Popover>
        </Box>
        )
      })}
    </Stack>
  )
}

const DesktopSubNav = ({ label, parentHref, href, subLabel }) => {
  return (
    <Link
      href={parentHref + href}
      role={"group"}
      display={"block"}
      p={2}
      rounded={"md"}
      _hover={{ bg: useColorModeValue("red.100", "gray.900") }}
    >
      <Stack direction={"row"} align={"center"}>
        <Box>
          <Text
            transition={"all .3s ease"}
            _groupHover={{ color: "red.400" }}
            fontWeight={500}
          >
            {label}
          </Text>
          <Text fontSize={"sm"}>{subLabel}</Text>
        </Box>
        <Flex
          transition={"all .3s ease"}
          transform={"translateX(-10px)"}
          opacity={0}
          _groupHover={{ opacity: "100%", transform: "translateX(0)" }}
          justify={"flex-end"}
          align={"center"}
          flex={1}
        >
          <Icon color={"red.400"} w={5} h={5} as={FiChevronRight} />
        </Flex>
      </Stack>
    </Link>
  )
}

const MobileNav = ({ onClose }) => {
  const location = useLocation()
  
  return (
    <Stack
      bg={useColorModeValue("white", "gray.800")}
      p={4}
      display={{ md: "none" }}
    >
      {NAV_ITEMS.map(navItem => (
        <MobileNavItem 
          key={navItem.label} 
          parentHref={navItem.href} 
          isActive={location.pathname === navItem.href}
          onClose={onClose}
          {...navItem} 
        />
      ))}
    </Stack>
  )
}

const MobileNavItem = ({ label, parentHref, children, href, isActive, onClose }) => {
  const { isOpen, onToggle } = useDisclosure()
  const activeBg = useColorModeValue("red.50", "gray.700")
  const activeColor = useColorModeValue("red.600", "red.300")
  const inactiveColor = useColorModeValue("gray.600", "gray.200")
  
  // Handle chevron click to toggle submenu (doesn't navigate)
  const handleChevronClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onToggle()
  }
  
  // Handle main link click - navigate and close menu
  const handleLinkClick = () => {
    if (onClose) {
      onClose()
    }
  }
  
  return (
    <Stack spacing={4}>
      <Flex
        py={2}
        px={3}
        justify={"space-between"}
        align={"center"}
        rounded="md"
        bg={isActive ? activeBg : "transparent"}
        borderLeft={isActive ? "4px solid" : "none"}
        borderColor={activeColor}
        _hover={{
          textDecoration: "none",
          bg: activeBg
        }}
      >
        <Box
          as={NavLink}
          to={href ?? "#"}
          onClick={handleLinkClick}
          flex={1}
          _hover={{ textDecoration: "none" }}
        >
          <Text
            fontWeight={isActive ? 700 : 600}
            color={isActive ? activeColor : inactiveColor}
          >
            {label}
          </Text>
        </Box>
        {children && (
          <IconButton
            aria-label="Expand submenu"
            icon={<Icon as={FiChevronRight} transition="all .25s ease-in-out" transform={isOpen ? "rotate(90deg)" : ""} />}
            variant="ghost"
            size="sm"
            onClick={handleChevronClick}
            color={inactiveColor}
            _hover={{ bg: "transparent" }}
          />
        )}
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={"solid"}
          borderColor={useColorModeValue("gray.200", "gray.700")}
          align={"start"}
        >
          {children &&
            children.map(child => (
              <Link 
                key={child.label} 
                py={2} 
                href={parentHref + child.href}
                onClick={onClose}
              >
                {child.label}
              </Link>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  )
}

const NAV_ITEMS = [
  {
    label: "Home",
    href: "/home",
    children: [
      {
        label: "New & Noteworthy",
        subLabel: "Up-and-coming events",
        href: "#new"
      }
    ]
  },
  {
    label: "Language",
    href: "/language",
    children: [
      {
        label: "History",
        subLabel: "Learn about the origins of our language",
        href: "#history"
      }
    ]
  },
  {
    label: "Dictionary",
    href: "/dictionary",
    children: [
      {
        label: "Browse Dictionary",
        subLabel: "Search and browse Hidatsa words",
        href: "#dictionary"
      }
    ]
  },
  {
    label: "Curriculum",
    href: "/curriculum",
    children: [
      {
        label: "Lessons",
        subLabel: "Start learning Hidatsa",
        href: "#lessons"
      },
    ]
  },
  {
    label: "Resources",
    href: "/resources",
    children: [
      {
        label: "Documents",
        subLabel: "Read documents about our project",
        href: "#docs"
      }
    ]
  },
  {
    label: "Contact Us",
    href: "/contact"
  }
]
